import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, ShieldAlert } from 'lucide-react';
import type { EventDisclosureReport, FullDisclosureRequest } from '../../types/disclosure';

function formTypeLabel(formType: string, isPaper?: boolean) {
  if (formType === 'minor_injury') return isPaper ? 'MI (paper)' : 'Minor Injury';
  if (formType === 'paracetamol_screening') return isPaper ? 'PAR (paper)' : 'Paracetamol';
  return isPaper ? 'PRF (paper)' : 'PRF';
}

interface DisclosureFullReportModalProps {
  open: boolean;
  eventName: string;
  shortReport: EventDisclosureReport | null;
  includeClinical: boolean;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (options: FullDisclosureRequest) => void;
}

export default function DisclosureFullReportModal({
  open,
  eventName,
  shortReport,
  includeClinical,
  busy,
  error,
  onCancel,
  onConfirm,
}: DisclosureFullReportModalProps) {
  const [reason, setReason] = useState('');
  const [fullIncidents, setFullIncidents] = useState(false);
  const [fullResources, setFullResources] = useState(false);
  const [fullPatientIds, setFullPatientIds] = useState<string[]>([]);

  const patients = useMemo(() => {
    if (!shortReport) return [];
    return shortReport.incidents.flatMap((inc) =>
      inc.prfs.map((p) => ({
        id: p.id,
        incidentId: inc.id,
        incidentTitle: inc.title,
        label: formTypeLabel(p.formType, p.isPaper),
      })),
    );
  }, [shortReport]);

  useEffect(() => {
    if (!open) return;
    setReason('');
    setFullIncidents(false);
    setFullResources(false);
    setFullPatientIds([]);
  }, [open]);

  if (!open) return null;

  const togglePatient = (id: string) => {
    setFullPatientIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const hasSelection = fullIncidents || fullResources || fullPatientIds.length > 0;
  const canSubmit = reason.trim().length >= 5 && hasSelection;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onConfirm({
      reason: reason.trim(),
      fullIncidents,
      fullResources,
      fullPatientIds,
    });
  };

  return (
    <div
      className="fixed inset-0 z-[400] flex items-center justify-center p-4 no-print"
      style={{ background: 'rgba(0,0,0,0.65)' }}
    >
      <div className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-card shadow-xl p-5 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
            <div>
              <h2 className="text-sm font-bold text-foreground">Full report</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Select the sections to include in the expanded report for{' '}
                <strong>{eventName}</strong>. Selected digital PRFs will be marked as disclosed
                with your justification.
              </p>
            </div>
          </div>
          <button type="button" onClick={onCancel} className="p-1 rounded hover:bg-accent" disabled={busy}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold text-foreground mb-1">Report sections</legend>

            <label className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2.5 cursor-pointer hover:bg-accent/40">
              <input
                type="checkbox"
                checked={fullIncidents}
                onChange={(e) => setFullIncidents(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="text-xs font-medium text-foreground block">Full incident information</span>
                <span className="text-[11px] text-muted-foreground">
                  All incident details, logs, remarks, tags, and assigned units
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2.5 rounded-lg border border-border px-3 py-2.5 cursor-pointer hover:bg-accent/40">
              <input
                type="checkbox"
                checked={fullResources}
                onChange={(e) => setFullResources(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="text-xs font-medium text-foreground block">Full resource data</span>
                <span className="text-[11px] text-muted-foreground">
                  Resource name, type, and MDT sign-on history (username)
                </span>
              </span>
            </label>

            <div className="rounded-lg border border-border px-3 py-2.5 space-y-2">
              <div>
                <span className="text-xs font-medium text-foreground block">Full patient information</span>
                <span className="text-[11px] text-muted-foreground">
                  Select individual PRF / MI / PAR records to include at full detail
                </span>
              </div>

              {!includeClinical ? (
                <p className="text-[11px] text-amber-400/90 bg-amber-500/10 border border-amber-500/20 rounded px-2 py-1.5">
                  Requires <strong>prfs:view</strong> permission to include clinical patient data.
                </p>
              ) : patients.length === 0 ? (
                <p className="text-[11px] text-muted-foreground">No patient records on this event.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-1 border border-border/60 rounded-md p-2">
                  {patients.map((p) => (
                    <label
                      key={p.id}
                      className="flex items-center gap-2 text-[11px] py-1 cursor-pointer hover:bg-accent/30 rounded px-1"
                    >
                      <input
                        type="checkbox"
                        checked={fullPatientIds.includes(p.id)}
                        onChange={() => togglePatient(p.id)}
                      />
                      <span className="font-mono font-semibold shrink-0">{p.id}</span>
                      <span className="text-muted-foreground shrink-0">{p.label}</span>
                      <span className="truncate text-muted-foreground ml-auto">{p.incidentId}</span>
                    </label>
                  ))}
                </div>
              )}

              {includeClinical && patients.length > 0 && (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFullPatientIds(patients.map((p) => p.id))}
                    className="text-[10px] text-primary hover:underline"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setFullPatientIds([])}
                    className="text-[10px] text-muted-foreground hover:underline"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          </fieldset>

          <div>
            <label className="text-xs font-medium text-foreground block mb-1">
              Justification <span className="text-muted-foreground">(required, min 5 characters)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-background border border-border text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary resize-none"
              placeholder="e.g. Post-event review for statutory reporting"
              autoFocus
            />
          </div>

          {!hasSelection && (
            <p className="text-[11px] text-muted-foreground">Select at least one section or patient record.</p>
          )}

          {error && (
            <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg text-xs border border-border hover:bg-accent disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy || !canSubmit}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              Generate full report
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
